// Phase 4/5b — the payroll approval & release refusals, and what the run page DOES about
// each. The toast always carries the server's own words (getApiErrorMessage); this module
// only decides the follow-up: reload the chain, offer Recalculate, or explain who must act.
// The friendly sentence is (a) the toast fallback when the server sent no text and (b) the
// headline of the notice the page keeps on screen after the toast is gone.
//
//   approval_stale          409  lines changed since an approval → approvals cleared → refresh
//   approval_conflict       409  the run changed mid-decision → refresh and try again
//   needs_recalc            409  last recalculation didn't finish → Recalculate
//   recalculating           409  a recalculation is running → wait, then refresh
//   group_approver_required 403  the stage needs Group Finance (access to every company)
//   segregation_of_duties   409  you approved/prepared it → someone else must act
//   already_in_settled_run  409  people in this run are already in another locked/paid payroll
//                                for the same period (Phase H double-pay guard) → send the run
//                                back to draft and recalculate; the server names the other payroll
//   forbidden (role)        403  'approver does not hold the role required for this approval
//                                level' → approver_role_required: someone holding that role acts
//   unpaid_leave_not_calculated 409  (lock) unpaid leave was approved/cancelled after the run was
//                                calculated: the server ALREADY cleared the approvals and sent
//                                the run back to validated (needs_recalc) → reload, Recalculate
//   unpaid_leave_held       409  (lock) unpaid-leave events payroll couldn't apply; nothing
//                                changed → review them in the Control Center, or lock again
//                                with override + reason (the same audited override as risk_blocked)
//
// refusedActionNotice() is what the run page uses: the coded notice when there is one, and
// for an approve/reject that failed any other way, a notice carrying the server's words —
// a refused approval must never be a silent no-op once the toast has faded.
import { getApiErrorMessage } from '@/lib/apiError';

export type PayrollAction = 'approve' | 'reject' | 'lock' | 'release' | 'mark_paid' | 'other';

export type PayrollErrorCode =
  | 'approval_stale'
  | 'approval_conflict'
  | 'needs_recalc'
  | 'recalculating'
  | 'group_approver_required'
  | 'segregation_of_duties'
  | 'approver_role_required'
  | 'already_in_settled_run'
  | 'unpaid_leave_not_calculated'
  | 'unpaid_leave_held'
  /** An approve/reject refused without one of the codes above (the server's words in detail). */
  | 'action_refused'
  /** An approve/reject with no answer, or a 5xx: the outcome is unknown, the chain is reloaded. */
  | 'action_uncertain';

export type FollowUp = 'refresh' | 'recalculate' | 'wait' | 'none';

export interface PayrollActionNotice {
  code: PayrollErrorCode;
  /** Headline shown on the page (and the toast fallback). */
  title: string;
  followUp: FollowUp;
  /** The server's own explanation, shown under the headline when it adds something. */
  detail?: string;
  /** The refusal itself changed the run on the server (e.g. approvals cleared): reload it so
   *  the page never keeps showing the state that no longer exists. */
  reload?: boolean;
}

const KNOWN: ReadonlySet<string> = new Set([
  'approval_stale',
  'approval_conflict',
  'needs_recalc',
  'recalculating',
  'group_approver_required',
  'segregation_of_duties',
  'approver_role_required',
  'already_in_settled_run',
  'unpaid_leave_not_calculated',
  'unpaid_leave_held',
]);

const ROLE_REFUSAL = /does not hold the role|role required/i;

export function payrollErrorCode(e: unknown): PayrollErrorCode | null {
  const res = (e as { response?: { status?: number; data?: { code?: unknown; message?: unknown; error?: { code?: unknown } } } })
    ?.response;
  const data = res?.data;
  const code = typeof data?.code === 'string' ? data.code : typeof data?.error?.code === 'string' ? data.error.code : null;
  // The approval-level role check answers with the generic 'forbidden' code; its message is
  // what tells it apart from any other 403.
  if (code === 'forbidden' && res?.status === 403 && ROLE_REFUSAL.test(String(data?.message ?? ''))) {
    return 'approver_role_required';
  }
  return code && KNOWN.has(code) ? (code as PayrollErrorCode) : null;
}

/** The server's own words for a refusal ('' when it sent none). */
const serverWords = (e: unknown) => getApiErrorMessage(e, '');

const isPaymentAction = (a: PayrollAction) => a === 'release' || a === 'mark_paid';

/** The notice for a refused payroll action, or null when the code isn't one of ours. */
export function payrollActionNotice(e: unknown, action: PayrollAction): PayrollActionNotice | null {
  const code = payrollErrorCode(e);
  switch (code) {
    case 'approval_stale':
      return {
        code,
        title: isPaymentAction(action)
          ? 'The payroll changed after it was approved, so it needs approving again before it can be paid.'
          : 'The payroll changed after it was approved, so it needs approving again.',
        followUp: 'refresh',
      };
    case 'approval_conflict':
      return {
        code,
        title: 'The payroll changed while you were working on it. The latest state has been loaded — try again.',
        followUp: 'refresh',
      };
    case 'needs_recalc':
      return {
        code,
        title: "The last recalculation of this payroll didn't finish. Recalculate it before continuing.",
        followUp: 'recalculate',
      };
    case 'recalculating':
      return {
        code,
        title: 'This payroll is being recalculated — try again when it finishes.',
        followUp: 'wait',
      };
    case 'approver_role_required':
      return {
        code,
        title:
          'You don’t hold the role this approval stage requires, so someone who does must approve it. ' +
          'The roles each stage needs are set in the approval policy.',
        followUp: 'none',
        detail: serverWords(e) || undefined,
      };
    case 'group_approver_required':
      return {
        code,
        title: 'This stage needs a Group Finance approver — someone with access to every company.',
        followUp: 'none',
      };
    case 'segregation_of_duties': {
      if (!isPaymentAction(action)) {
        return {
          code,
          title: 'You have already acted on this run, so a different person must approve this stage.',
          followUp: 'none',
        };
      }
      // Two release rules share the code: the run policy's "releaser must not be an
      // approver" (message names approving) and the batch's "submitter ≠ preparer".
      const msg = String((e as { response?: { data?: { message?: unknown } } })?.response?.data?.message ?? '');
      const preparerOnly = /preparer/i.test(msg) && !/approv/i.test(msg);
      return {
        code,
        title: preparerOnly
          ? 'You prepared this payment, so someone else must release it.'
          : 'You approved this run, so someone else must release the payment.',
        followUp: 'none',
      };
    }
    case 'already_in_settled_run':
      return {
        code,
        title: isPaymentAction(action)
          ? 'This payroll can’t be paid: some of its employees are already in another locked or paid payroll for the same period. Paying it would pay them twice.'
          : 'This payroll can’t be locked: some of its employees are already in another locked or paid payroll for the same period. Locking it would pay them twice.',
        followUp: 'none',
        // The server names the other payroll (company, period, run) and what to do.
        detail: serverWords(e) || undefined,
      };
    case 'unpaid_leave_not_calculated':
      return {
        code,
        title:
          'Unpaid leave changed after this payroll was calculated, so it was sent back for recalculation and its approvals were cleared. Recalculate it, then approve it again.',
        followUp: 'recalculate',
        reload: true,
        detail: serverWords(e) || undefined,
      };
    case 'unpaid_leave_held':
      return {
        code,
        title: 'This payroll can’t be locked yet: some unpaid leave could not be applied, so paying it now would leave that leave out.',
        followUp: 'none',
        // The server counts the events and people, and says where to review them.
        detail: serverWords(e) || undefined,
      };
    default:
      return null;
  }
}

/** A lock refusal an approver may override with an audited reason. */
export interface LockOverrideRefusal {
  kind: 'risk_blocked' | 'unpaid_leave_held';
  /** The server's words ('' when it sent none — the dialog then uses its own). */
  message: string;
}

/** 409 risk_blocked / unpaid_leave_held on lock → the override dialog; anything else → null.
 *  Both are lifted by the same `override` + `override_reason` on a second lock call. */
export function lockOverrideRefusal(e: unknown): LockOverrideRefusal | null {
  const res = (e as { response?: { status?: number; data?: { code?: unknown; message?: unknown } } })?.response;
  if (res?.status !== 409) return null;
  const code = res.data?.code;
  if (code !== 'risk_blocked' && code !== 'unpaid_leave_held') return null;
  return { kind: code, message: typeof res.data?.message === 'string' ? res.data.message : '' };
}

/**
 * The notice the run page keeps on screen after a refused action: the coded notice when
 * there is one; otherwise, for approve/reject, one that carries the server's words (any
 * 4xx), or — when nothing came back / a 5xx — says the outcome is unknown and reloads the
 * chain (an approval may have landed; "try again" could otherwise double-act). Other actions
 * keep their existing behaviour (null → toast only).
 */
export function refusedActionNotice(e: unknown, action: PayrollAction): PayrollActionNotice | null {
  const coded = payrollActionNotice(e, action);
  if (coded) return coded;
  if (action !== 'approve' && action !== 'reject') return null;
  const verb = action === 'approve' ? 'approval' : 'rejection';
  const status = (e as { response?: { status?: number } })?.response?.status;
  if (typeof status === 'number' && status >= 400 && status < 500) {
    return {
      code: 'action_refused',
      title: `The ${verb} was not recorded.`,
      followUp: 'none',
      detail: serverWords(e) || undefined,
    };
  }
  return {
    code: 'action_uncertain',
    title: `We couldn’t confirm whether the ${verb} was recorded. The approval chain has been reloaded — check it before trying again.`,
    followUp: 'refresh',
    detail: serverWords(e) || undefined,
  };
}
